import { API } from '../api.js';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import { Globe, Plus, Trash2, RotateCcw, Search, Save, Check, X, AlertTriangle, Languages, Star, Filter } from 'lucide-react';
import Paginator from '../components/Paginator';
import ConfirmDialog from '../components/ConfirmDialog';
import styles from './LangEditorTab.module.css';

export default function LangEditorTab({ onToast }) {
  const { token } = useAuth();
  const { t, allLanguages, reload, reloadLanguages } = useLang();
  
  // Translation Keys States
  const [activeLang, setActiveLang] = useState('en');
  const [keys, setKeys]   = useState([]);
  const [filter, setFilter] = useState('');
  const [filterState, setFilterState] = useState('all'); // 'all' | 'custom' | 'overridden' | 'modified'
  const [edits, setEdits] = useState({});  // key -> new value
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modals / Inline Forms States
  const [showAddLang, setShowAddLang] = useState(false);
  const [newLang, setNewLang] = useState({ code: '', name: '', flag: '🌐' });
  const [showAddKey, setShowAddKey] = useState(false);
  const [newKey, setNewKey] = useState({ key: '', value: '' });

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: '',
    cancelText: '',
    onConfirm: null,
    type: 'warning'
  });

  // Load translation keys for the selected language
  const loadKeys = useCallback(async (langCode) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/i18n/${langCode}/flat`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      if (res.ok) {
        setKeys(await res.json());
      } else {
        onToast('Failed to load language keys');
      }
    } catch (e) {
      console.error('Failed to load keys:', e);
      onToast('Network error while loading languages');
    } finally {
      setLoading(false);
      setEdits({});
      setCurrentPage(1);
    }
  }, [token, onToast]);

  useEffect(() => { 
    loadKeys(activeLang); 
  }, [activeLang, loadKeys]);

  // Handle value change for a key
  function handleEdit(key, value) { 
    setEdits(e => ({ ...e, [key]: value })); 
  }
  
  // Revert single local change
  function revert(key) { 
    setEdits(e => { 
      const n = { ...e }; 
      delete n[key]; 
      return n; 
    }); 
  }

  // Save all key translation overrides
  async function saveAll() {
    const overrides = Object.entries(edits).map(([key, value]) => ({ key, value }));
    if (!overrides.length) { 
      onToast('No changes to save'); 
      return; 
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/i18n/${activeLang}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json', 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ overrides }),
      });
      if (res.ok) {
        await loadKeys(activeLang);
        reload(); // Refresh the client translations
        onToast(t('langEditor.saved', 'Translations saved!'));
      } else {
        const data = await res.json();
        onToast(data.error || t('common.error', 'Error occurred'));
      }
    } catch (e) {
      onToast(t('common.error', 'Error occurred'));
    } finally {
      setSaving(false);
    }
  }

  // Language CRUD Actions
  async function handleAddLanguage(e) {
    e.preventDefault();
    if (!newLang.code.trim() || !newLang.name.trim()) {
      onToast('Code and name are required');
      return;
    }
    try {
      const res = await fetch(`${API}/api/i18n/languages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          code: newLang.code.trim().toLowerCase(),
          name: newLang.name.trim(),
          flag: newLang.flag.trim(),
          is_active: true,
          is_default: false
        })
      });
      if (res.ok) {
        onToast('Language added successfully!');
        setNewLang({ code: '', name: '', flag: '🌐' });
        setShowAddLang(false);
        await reloadLanguages();
      } else {
        const err = await res.json();
        onToast(err.error || 'Failed to add language');
      }
    } catch (e) {
      onToast('Network error while adding language');
    }
  }

  async function toggleLangActive(code, currentActive) {
    try {
      const res = await fetch(`${API}/api/i18n/languages/${code}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !currentActive })
      });
      if (res.ok) {
        onToast(`Language ${code} ${!currentActive ? 'activated' : 'deactivated'}`);
        await reloadLanguages();
        reload(); // Reload i18n
      }
    } catch (e) {
      onToast('Failed to toggle language status');
    }
  }

  async function setLangDefault(code) {
    try {
      const res = await fetch(`${API}/api/i18n/languages/${code}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ is_default: true, is_active: true })
      });
      if (res.ok) {
        onToast(`Language ${code} is now the default language`);
        await reloadLanguages();
        reload(); // Reload i18n
      }
    } catch (e) {
      onToast('Failed to set default language');
    }
  }

  function triggerDeleteLanguage(code) {
    if (code === 'en') {
      onToast('Cannot delete English (base language)');
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: 'Delete Language Pack',
      message: `Are you sure you want to delete ${code.toUpperCase()}? This will permanently delete all custom overridden translation keys for this language.`,
      confirmText: 'Delete',
      cancelText: t('common.cancel', 'Cancel'),
      type: 'danger',
      onConfirm: () => confirmDeleteLanguage(code)
    });
  }

  async function confirmDeleteLanguage(code) {
    try {
      const res = await fetch(`${API}/api/i18n/languages/${code}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        onToast('Language deleted');
        if (activeLang === code) setActiveLang('en');
        await reloadLanguages();
        reload(); // Reload i18n
      }
    } catch (e) {
      onToast('Failed to delete language');
    } finally {
      setConfirmDialog(prev => ({ ...prev, isOpen: false }));
    }
  }

  // Key CRUD Actions
  async function handleAddKey(e) {
    e.preventDefault();
    if (!newKey.key.trim() || !newKey.value.trim()) {
      onToast('Key path and default English value are required');
      return;
    }
    const cleanKey = newKey.key.trim();
    const cleanValue = newKey.value.trim();

    try {
      // Save key override directly to active language (or default to EN)
      const res = await fetch(`${API}/api/i18n/en`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          overrides: [{ key: cleanKey, value: cleanValue }]
        })
      });

      if (res.ok) {
        // Also save for current activeLang if it is different from EN
        if (activeLang !== 'en') {
          await fetch(`${API}/api/i18n/${activeLang}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              overrides: [{ key: cleanKey, value: cleanValue }]
            })
          });
        }

        onToast('Custom translation key added!');
        setNewKey({ key: '', value: '' });
        setShowAddKey(false);
        await loadKeys(activeLang);
        reload();
      } else {
        const err = await res.json();
        onToast(err.error || 'Failed to add translation key');
      }
    } catch (e) {
      onToast('Failed to save translation key');
    }
  }

  function triggerDeleteKey(keyName) {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Translation Key',
      message: `Are you sure you want to delete key "${keyName}" completely from the system?`,
      confirmText: 'Delete',
      cancelText: t('common.cancel', 'Cancel'),
      type: 'danger',
      onConfirm: () => confirmDeleteKey(keyName)
    });
  }

  async function confirmDeleteKey(keyName) {
    try {
      const res = await fetch(`${API}/api/i18n/keys/${keyName}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        onToast('Translation key deleted successfully');
        await loadKeys(activeLang);
        reload();
      } else {
        onToast('Failed to delete key');
      }
    } catch (e) {
      onToast('Failed to delete key');
    } finally {
      setConfirmDialog(prev => ({ ...prev, isOpen: false }));
    }
  }

  // Filter keys logic
  const filtered = keys.filter(k => {
    const matchesSearch = !filter || 
      k.key.toLowerCase().includes(filter.toLowerCase()) || 
      (k.current && k.current.toLowerCase().includes(filter.toLowerCase())) ||
      (k.base_value && k.base_value.toLowerCase().includes(filter.toLowerCase()));

    let matchesState = true;
    if (filterState === 'custom') matchesState = k.is_custom;
    else if (filterState === 'overridden') matchesState = !!k.override;
    else if (filterState === 'modified') matchesState = edits[k.key] !== undefined;

    return matchesSearch && matchesState;
  });

  // Paginated keys
  const paginatedKeys = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const dirtyCount = Object.keys(edits).length;

  return (
    <div className="flex-col" style={{ gap: 20 }}>
      {/* 🌐 PREMIUM LANGUAGES MANAGEMENT DASHBOARD */}
      <div className="card" style={{ backdropFilter: 'blur(10px)', border: '1px solid var(--border)' }}>
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16 }}>
            <Languages size={18} className="text-primary" />
            Language Management
          </div>
          <button 
            className="btn btn-primary btn-sm" 
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            onClick={() => setShowAddLang(!showAddLang)}
          >
            {showAddLang ? <X size={14} /> : <Plus size={14} />}
            {showAddLang ? 'Cancel' : 'Add Language'}
          </button>
        </div>

        {showAddLang && (
          <form onSubmit={handleAddLanguage} style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', background: 'var(--surface2)' }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div className="form-group" style={{ flex: 1, minWidth: 100 }}>
                <label className="form-label">ISO Code (e.g. ja)</label>
                <input 
                  className="form-input" 
                  maxLength={5} 
                  required 
                  placeholder="ja"
                  value={newLang.code} 
                  onChange={e => setNewLang(l => ({ ...l, code: e.target.value }))} 
                />
              </div>
              <div className="form-group" style={{ flex: 2, minWidth: 150 }}>
                <label className="form-label">Language Name (e.g. Japanese)</label>
                <input 
                  className="form-input" 
                  required 
                  placeholder="Japanese"
                  value={newLang.name} 
                  onChange={e => setNewLang(l => ({ ...l, name: e.target.value }))} 
                />
              </div>
              <div className="form-group" style={{ flex: 1, minWidth: 80 }}>
                <label className="form-label">Flag Emoji</label>
                <input 
                  className="form-input" 
                  required 
                  value={newLang.flag} 
                  onChange={e => setNewLang(l => ({ ...l, flag: e.target.value }))} 
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button type="submit" className="btn btn-primary" style={{ height: 38 }}>
                  Register Language
                </button>
              </div>
            </div>
          </form>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Language</th>
                <th>ISO Code</th>
                <th>Status</th>
                <th>Role Tier</th>
                <th>System Operations</th>
              </tr>
            </thead>
            <tbody>
              {allLanguages && allLanguages.map(l => (
                <tr key={l.code}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
                      <span style={{ fontSize: 18 }}>{l.flag}</span>
                      <span>{l.name}</span>
                    </div>
                  </td>
                  <td>
                    <code style={{ fontSize: 11, background: 'var(--surface2)', padding: '2px 6px', borderRadius: 4 }}>
                      {l.code}
                    </code>
                  </td>
                  <td>
                    <span className={`badge ${l.is_active ? 'badge-active' : 'badge-inactive'}`}>
                      {l.is_active ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td>
                    {l.is_default ? (
                      <span className="badge badge-default" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Star size={11} fill="currentColor" /> Default Pack
                      </span>
                    ) : (
                      <button className="btn btn-ghost btn-xs" onClick={() => setLangDefault(l.code)}>
                        Set Default
                      </button>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button 
                        className="btn btn-ghost btn-xs" 
                        onClick={() => toggleLangActive(l.code, l.is_active)}
                        disabled={l.is_default}
                      >
                        {l.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button 
                        className="btn btn-ghost btn-xs text-danger" 
                        onClick={() => triggerDeleteLanguage(l.code)}
                        disabled={l.is_default || l.code === 'en'}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 📝 TRANSLATION KEYS EDITOR CARD */}
      <div className="card" style={{ border: '1px solid var(--border)' }}>
        <div className="card-header" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16 }}>
            <Globe size={18} className="text-accent" />
            {t('langEditor.title', 'Language Editor')}
          </div>
          
          <div className="flex-center" style={{ gap: 12, flexWrap: 'wrap' }}>
            {/* Language Selector tabs */}
            <div className={styles.langTabs}>
              {allLanguages && allLanguages.map(l => (
                <button
                  key={l.code}
                  className={`${styles.langTab} ${activeLang === l.code ? styles.activeTab : ''}`}
                  onClick={() => setActiveLang(l.code)}
                >
                  {l.flag} {l.name}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <select
                value={filterState}
                onChange={e => { setFilterState(e.target.value); setCurrentPage(1); }}
                style={{
                  background: 'var(--surface2)',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                  fontSize: 12,
                  padding: '0 8px',
                  borderRadius: 6,
                  height: 32,
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Keys</option>
                <option value="custom">Custom Keys</option>
                <option value="overridden">Overridden Keys</option>
                <option value="modified">Unsaved Changes</option>
              </select>
            </div>

            <div className="search-box" style={{ width: 180, height: 32 }}>
              <span>🔍</span>
              <input 
                value={filter} 
                onChange={e => { setFilter(e.target.value); setCurrentPage(1); }} 
                placeholder={t('langEditor.filter', 'Filter keys…')} 
                style={{ height: '100%', fontSize: 12 }}
              />
            </div>

            <button 
              className="btn btn-outline btn-sm"
              style={{ display: 'flex', alignItems: 'center', gap: 4, height: 32 }}
              onClick={() => setShowAddKey(!showAddKey)}
            >
              {showAddKey ? <X size={14} /> : <Plus size={14} />}
              New Key
            </button>

            {dirtyCount > 0 && (
              <button className="btn btn-primary btn-sm" style={{ height: 32, display: 'flex', alignItems: 'center', gap: 6 }} onClick={saveAll} disabled={saving}>
                <Save size={14} />
                {saving ? t('common.loading', 'Saving…') : `Save (${dirtyCount})`}
              </button>
            )}
          </div>
        </div>

        {showAddKey && (
          <form onSubmit={handleAddKey} style={{ padding: 16, background: 'var(--surface2)', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 12 }}>
              <div className="flex-col" style={{ gap: 4, flex: 1 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)' }}>Key Path (e.g. dashboard.welcomeMessage)</label>
                <input 
                  type="text" 
                  required
                  className={styles.editInput}
                  value={newKey.key}
                  onChange={e => setNewKey(prev => ({ ...prev, key: e.target.value }))}
                  placeholder="dashboard.welcomeMessage"
                />
              </div>
              <div className="flex-col" style={{ gap: 4, flex: 2 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)' }}>Default English Value</label>
                <input 
                  type="text" 
                  required
                  className={styles.editInput}
                  value={newKey.value}
                  onChange={e => setNewKey(prev => ({ ...prev, value: e.target.value }))}
                  placeholder="Welcome back to Skool!"
                />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="submit" className="btn btn-primary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Check size={14} /> Add Translation Key
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--text2)' }}>
            <span style={{ fontSize: 14 }}>🌀 {t('common.loading', 'Loading…')}</span>
          </div>
        ) : (
          <>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th style={{ width: '30%' }}>{t('langEditor.key', 'Key')}</th>
                    <th style={{ width: '33%' }}>Base Value (File / Fallback)</th>
                    <th style={{ width: '30%' }}>Translation Override</th>
                    <th style={{ width: '7%', textAlign: 'center' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedKeys.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>
                        No matching translation keys found.
                      </td>
                    </tr>
                  ) : (
                    paginatedKeys.map(row => {
                      const editVal    = edits[row.key];
                      const isEditing  = editVal !== undefined;
                      const hasOverride = !!row.override;
                      const current    = isEditing ? editVal : row.current;

                      return (
                        <tr key={row.key} className={isEditing ? styles.dirtyRow : hasOverride ? styles.overrideRow : ''}>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <code className={styles.keyCode}>{row.key}</code>
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {row.is_custom && (
                                  <span className="badge badge-accent" style={{ fontSize: 9, padding: '1px 6px', background: 'rgba(67,233,123,0.12)', color: 'var(--accent3)' }}>custom</span>
                                )}
                                {hasOverride && !isEditing && (
                                  <span className={styles.overrideBadge}>overridden</span>
                                )}
                                {isEditing && (
                                  <span className={styles.editingBadge}>editing</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className={styles.baseVal}>
                            {row.base_value || <span style={{ opacity: 0.4, fontStyle: 'italic' }}>no base value</span>}
                          </td>
                          <td>
                            <textarea
                              className={styles.editInput}
                              value={current}
                              onChange={e => handleEdit(row.key, e.target.value)}
                              rows={Math.max(1, Math.ceil((current || '').length / 50))}
                              placeholder={row.base_value ? `Default: ${row.base_value}` : "Write translation here..."}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                              {(isEditing || hasOverride) && (
                                <button
                                  className={styles.revertBtn}
                                  title="Revert to base value"
                                  onClick={() => {
                                    if (isEditing) revert(row.key);
                                    else handleEdit(row.key, ''); // empty override triggers delete override in backend
                                  }}
                                  style={{ padding: 4 }}
                                >
                                  <RotateCcw size={13} />
                                </button>
                              )}
                              {row.is_custom && (
                                <button
                                  className="btn btn-ghost btn-xs text-danger"
                                  title="Delete key completely"
                                  onClick={() => triggerDeleteKey(row.key)}
                                  style={{ padding: 4 }}
                                >
                                  <Trash2 size={13} style={{ color: 'var(--accent2)' }} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <Paginator
              totalItems={filtered.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </>
        )}
      </div>

      <div style={{ fontSize: 12, color: 'var(--text3)', padding: '0 4px', display: 'flex', justifyContent: 'space-between' }}>
        <span>📝 {filtered.length} keys shown · {dirtyCount} unsaved changes</span>
        <span>Empty override = revert to base file value</span>
      </div>

      {/* Confirm Dialog Overlay */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        type={confirmDialog.type}
      />
    </div>
  );
}
